/**
 * A file the viewer attached, in their own message (owner 2026-11-01): an image shows as a thumbnail that opens full
 * size, a document as its name with Download. Only its owner can read it (`GET /uploads/{id}/content` answers anyone
 * else not found), so this is drawn in the viewer's own messages only; another person's is `OthersMessageAttachment`.
 * Demo page: NextAdmin `/chats` (an image and a file in a sent message).
 */
import { useAuiState } from '@assistant-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileText } from 'lucide-react';
import { useState } from 'react';
import { getUploadContentOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import { useObjectUrl } from '../../hooks/useObjectUrl.js';
import { Button } from '../ui/Button.js';
import { Modal } from '../ui/Modal.js';
import { Skeleton } from '../ui/Skeleton.js';
import { useToast } from '../ui/Toast.js';
import { uploadIdOfAttachment } from '../chat/message-attachment-ids.js';

/** The file's bytes, kept for the session: an upload never changes. */
const contentOptions = (uploadId: number) => ({
  ...getUploadContentOptions({ path: { id: uploadId }, parseAs: 'blob' }),
  staleTime: Number.POSITIVE_INFINITY,
});

/** Saves a blob under its name: a link the browser follows once, made and dropped in script (never rendered). */
function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function Unavailable({ name }: { name: string }) {
  return <p className="rounded-lg border border-border px-3 py-2 text-xs text-muted">{name} — unavailable</p>;
}

function OwnImage({ uploadId, name }: { uploadId: number; name: string }) {
  const [open, setOpen] = useState(false);
  const content = useQuery(contentOptions(uploadId));
  const preview = useObjectUrl(content.data instanceof Blob ? content.data : null);
  if (content.isError) return <Unavailable name={name} />;
  return (
    <>
      <Button
        variant="ghost"
        className="h-auto p-0"
        aria-label={`Open ${name}`}
        onClick={() => setOpen(true)}
        disabled={!preview}
      >
        {preview ? (
          <img src={preview} alt={name} className="max-h-48 max-w-64 rounded-lg border border-border object-contain" />
        ) : (
          <Skeleton width={192} height={128} rounded="rounded-lg" />
        )}
      </Button>
      <Modal open={open} onClose={() => setOpen(false)} title={name} maxWidth="full">
        {preview && <img src={preview} alt={name} className="mx-auto max-h-[80vh] object-contain" />}
      </Modal>
    </>
  );
}

function OwnDocument({ uploadId, name }: { uploadId: number; name: string }) {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const download = async () => {
    try {
      const data = await queryClient.fetchQuery(contentOptions(uploadId));
      if (!(data instanceof Blob)) throw new Error('The file could not be downloaded.');
      saveBlob(data, name);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'The file could not be downloaded.', 'danger');
    }
  };
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-xs">
      <FileText size={16} className="shrink-0 text-muted" aria-hidden />
      <span className="max-w-56 truncate font-medium text-heading">{name}</span>
      <Button variant="ghost" size="sm" iconOnly aria-label={`Download ${name}`} onClick={() => void download()}>
        <Download size={14} aria-hidden />
      </Button>
    </div>
  );
}

export function OwnMessageAttachment() {
  const attachment = useAuiState((s) => s.attachment);
  const uploadId = uploadIdOfAttachment(attachment.id);
  if (uploadId === null) return <Unavailable name={attachment.name} />;
  return attachment.type === 'image' ? (
    <OwnImage uploadId={uploadId} name={attachment.name} />
  ) : (
    <OwnDocument uploadId={uploadId} name={attachment.name} />
  );
}
